import type { ApiClient } from "./api/client";
import type { TokenStore } from "./auth/token-store";

/**
 * Contexte applicatif — l'équivalent minimal d'un `ApplicationContext` Spring.
 * ==========================================================================
 *
 * ── Le problème ─────────────────────────────────────────────────────────────
 * Une feature a besoin du client HTTP et de la session. Ces objets sont construits par
 * `apps/app`, qui seul connaît l'environnement. Mais une feature **ne doit jamais importer
 * `@starter/app`** : ce serait une dépendance circulaire, et surtout ça casserait le prune
 * (retirer une feature ne doit rien exiger de plus que supprimer son package).
 *
 * ── La solution ─────────────────────────────────────────────────────────────
 * Le shell *fournit* le contexte au démarrage, les features le *consomment*. La dépendance
 * ne va que dans un sens : `feature-* → core`, jamais l'inverse ni de côté.
 *
 *   // apps/app/src/main.ts
 *   provideContext({ api, tokenStore });
 *
 *   // packages/feature-auth/src/data/auth-api.ts
 *   const { api } = useContext();
 *
 * ── Pourquoi si rudimentaire ────────────────────────────────────────────────
 * Un vrai conteneur d'injection serait du luxe pour cinq features. L'important n'est pas le
 * mécanisme, c'est **le sens de la dépendance** — et un module unique suffit à l'imposer.
 */
export interface AppContext {
  readonly api: ApiClient;
  readonly tokenStore: TokenStore;
  /**
   * URL du back. Exposée ici parce qu'une feature ne doit **jamais** lire
   * `import.meta.env` : seul le shell connaît l'environnement. Sert aux rares cas qui ne
   * passent pas par le client généré — le flux SSE par exemple, qui est une connexion
   * longue et non une requête.
   */
  readonly baseUrl: string;
}

let current: AppContext | null = null;

/** Appelé une fois par le shell, avant tout rendu. */
export function provideContext(context: AppContext): void {
  current = context;
}

/**
 * Récupère le contexte. Lève si le shell ne l'a pas fourni — un échec explicite au
 * démarrage vaut mieux qu'un `undefined` qui se propage jusqu'à un écran vide.
 */
export function useContext(): AppContext {
  if (current === null) {
    throw new Error(
      "AppContext absent : apps/app doit appeler provideContext() avant de monter un écran.",
    );
  }
  return current;
}

/** Réservé aux tests unitaires, pour repartir d'un état propre. */
export function resetContext(): void {
  current = null;
}
